/**
 * Inline markers wrapping selected slash-command lookup entities inside a
 * submitted message string. The host wraps each picked entity name like:
 *
 *     "what is CORONA sales"   ->   "what is /*CORONA*\/ sales"
 *
 * The marker exists on the wire (so the LLM can spot entity boundaries inside
 * free text) and in stored chat history, but UI surfaces should not show the
 * `/*` and `*\/` chars. The helpers below let renderers parse those marker
 * tokens out for highlighted display or strip them down to plain text.
 *
 * Note: the textarea content NO LONGER contains a leading `/` for confirmed
 * picks — the design lib hides the slash entirely once the user accepts a
 * suggestion (see `useSlashLookup.accept`). `wrapSlashTokensInMessage` is
 * therefore a word-boundary match against the bare entity name rather than
 * `/EntityName`.
 */

const MARKER_PREFIX = '/*';
const MARKER_SUFFIX = '*/';

/** Regex that captures the inner entity name. Group 1 is the entity text. */
export const LOOKUP_TOKEN_MARKER_RE = /\/\*([^*]+?)\*\//g;

/** Wrap an entity name with inline markers, e.g. `"CORONA"` -> `"/*CORONA*\/"`. */
export function wrapLookupToken(entityName: string): string {
    return `${MARKER_PREFIX}${entityName}${MARKER_SUFFIX}`;
}

/**
 * Remove inline lookup-token markers from a string, keeping only the inner
 * entity names. Useful for plain-text contexts like accordion titles where
 * we cannot render JSX (`"what is /*CORONA*\/ sales"` -> `"what is CORONA sales"`).
 */
export function stripLookupTokenMarkers(text: string): string {
    if (!text || text.indexOf(MARKER_PREFIX) === -1) return text;
    return text.replace(LOOKUP_TOKEN_MARKER_RE, (_match, inner: string) => inner);
}

/** A parsed text segment: literal text or an entity that should be highlighted. */
export interface LookupTextSegment {
    type: 'text' | 'entity';
    text: string;
}

/**
 * Split a string into text and entity segments based on inline markers.
 * Renderers can iterate segments to draw plain text vs highlighted entity
 * pills without the marker chars being visible.
 */
export function parseLookupTokens(text: string): LookupTextSegment[] {
    if (!text) return [{ type: 'text', text: '' }];
    if (text.indexOf(MARKER_PREFIX) === -1) return [{ type: 'text', text }];

    const segments: LookupTextSegment[] = [];
    const re = new RegExp(LOOKUP_TOKEN_MARKER_RE.source, 'g');
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
        if (match.index > lastIndex) {
            segments.push({ type: 'text', text: text.slice(lastIndex, match.index) });
        }
        segments.push({ type: 'entity', text: match[1] });
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
        segments.push({ type: 'text', text: text.slice(lastIndex) });
    }
    return segments;
}

/**
 * Wrap each occurrence of a selected entity name with the inline marker form.
 * Only entity names present in `entityNames` are wrapped; other text in the
 * message stays untouched.
 *
 * Matching:
 *   - **Left boundary**: start of string OR any non-word character that is
 *     also NOT `*` (so we never re-wrap an entity that already sits inside
 *     `/*...*\/`).
 *   - **Right boundary**: end of string OR any non-word character (whitespace
 *     or punctuation) so an entity name doesn't match the prefix of a larger
 *     identifier.
 *
 * All entity names are folded into a single regex alternation in
 * length-descending order so the leftmost-longest match wins at any given
 * position (e.g. `Coca Cola` beats `Coca`), and only ONE pass over the string
 * is made — preventing already-wrapped tokens from being re-wrapped.
 */
export function wrapSlashTokensInMessage(message: string, entityNames: string[]): string {
    if (!message || entityNames.length === 0) return message;
    const unique = [...new Set(entityNames.filter(Boolean))].sort((a, b) => b.length - a.length);
    if (unique.length === 0) return message;
    const alternation = unique
        .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|');
    // `(^|[^A-Za-z0-9_*])` — left boundary char (or empty at SOS). Excluding
    // `*` keeps us from matching the entity name *inside* a `/*name*\/` block
    // produced earlier in this same pass / in a previous render.
    const re = new RegExp(`(^|[^A-Za-z0-9_*])(${alternation})(?=$|[^A-Za-z0-9_])`, 'g');
    return message.replace(
        re,
        (_match, prefix: string, name: string) => `${prefix}${wrapLookupToken(name)}`,
    );
}
